import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Key, Copy, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CreateClient = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    password: ''
  });
  
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [createdClient, setCreatedClient] = useState(null);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    if (!password) {
      navigate('/admin/login');
    }
    // Generate a random password
    generatePassword();
  }, []);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let pwd = '';
    for (let i = 0; i < 12; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password: pwd }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password) {
      setError('Name, email, and password are required');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const client = await response.json();
        setCreatedClient({ ...client, password: formData.password });
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to create client');
      }
    } catch (err) {
      setError('Failed to create client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const copyCredentials = () => {
    const text = `Pawsome Ventures Client Portal Access:\n\nEmail: ${createdClient.email}\nPassword: ${createdClient.password}\n\nLogin at: ${window.location.origin}/client/login`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (createdClient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-border p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Client Created!</h2>
            <p className="text-gray-600">Portal access has been set up</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Key className="w-4 h-4" />
              Login Credentials
            </h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Email:</span>
                <p className="font-mono font-medium text-gray-900">{createdClient.email}</p>
              </div>
              <div>
                <span className="text-gray-600">Password:</span>
                <p className="font-mono font-medium text-gray-900">{createdClient.password}</p>
              </div>
              <div className="pt-2">
                <span className="text-gray-600">Portal URL:</span>
                <p className="font-mono text-xs text-gray-900 break-all">{window.location.origin}/client/login</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 text-sm text-yellow-800">
            <strong>⚠️ Important:</strong> Save these credentials! You won't see the password again. Send them to the client via secure communication.
          </div>

          <div className="flex gap-3">
            <button
              onClick={copyCredentials}
              className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Credentials'}
            </button>
            <button
              onClick={() => navigate('/admin/clients')}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary">Create Client Account</h1>
              <p className="text-sm text-foreground/60">Set up portal access for a new client</p>
            </div>
            <button
              onClick={() => navigate('/admin/clients')}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold mb-4">Client Information</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Sarah"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Johnson"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="client@example.com"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+1 345 925 1234"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows="2"
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Full address"
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Key className="w-5 h-5" />
              Portal Login
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password (Auto-generated)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                />
                <button
                  type="button"
                  onClick={generatePassword}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-gray-50"
                >
                  Regenerate
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1">Client can change this after first login</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/clients')}
              className="flex-1 px-6 py-3 border border-border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Creating...' : 'Create Client Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
